/**
 * Journaled Candid document write-back payload.
 *
 * Coverage-service stores what it posted to Candid:
 * `{ documents: [{ gcs_path, display_name, image_id }] }`.
 * The preview UI uses {@link CandidDocumentPayload} (`document_gcs_path`,
 * `display_name`). {@link CandidDocumentWritebackPayload.parse} unwraps the
 * envelope and remaps `gcs_path`; flatter request-shaped rows still parse.
 *
 * @module candidDocumentWritebackPayload
 * @example
 * const doc = CandidDocumentWritebackPayload.parse(record.payload);
 */

/**
 * One document line as dumped into the write-back `payload` column.
 */
export interface CandidJournalDocumentEntry {
  /** Bucket-relative GCS object path posted to Candid. */
  gcs_path: string;
  /** Attachment name shown in Candid; null when derived from the file name. */
  display_name: string | null;
  /** Candid image id returned after upload. */
  image_id: string;
}

/**
 * `payload` column for a document write-back row.
 */
export interface CandidDocumentWritebackEnvelope {
  /** Documents posted on this attempt. */
  documents: CandidJournalDocumentEntry[];
}

/**
 * Document body the preview tab consumes.
 *
 * Matches the coverage-service request (`document_gcs_path`, `display_name`).
 * `image_id` is journal-only and is not carried here.
 */
export interface CandidDocumentPayload {
  /** Bucket-relative object path of the approval document. */
  document_gcs_path: string;
  /**
   * Name shown on the Candid attachment. When omitted or blank, Candid
   * derives it from the file name in `document_gcs_path`.
   */
  display_name: string | null;
}

/**
 * Parses a write-back `payload` into the document the preview tab consumes.
 *
 * Accepts the journal envelope, a bare array, a single journal line, or a
 * row already stored in {@link CandidDocumentPayload} shape. One journal
 * row currently records one document; extra `documents[]` entries are ignored.
 */
export class CandidDocumentWritebackPayload {
  /**
   * Unwraps a stored document `payload` and returns the first usable body.
   *
   * @param payload - Write-back `payload` column value
   * @returns Preview payload, or `null` when none has a path
   */
  static parse(payload: unknown): CandidDocumentPayload | null {
    for (const entry of this.unwrapEntries(payload)) {
      const next = this.toPreview(entry);
      if (next) {
        return next;
      }
    }
    return null;
  }

  /**
   * Pulls typed document objects out of the stored column.
   *
   * @param payload - Envelope, array, or single entry
   * @returns Journal lines or already-flat preview bodies
   */
  private static unwrapEntries(payload: unknown): StoredDocumentEntry[] {
    const envelope = asRecord(payload);
    const source = envelope?.["documents"] ?? payload;
    return (Array.isArray(source) ? source : [source]).filter(isStoredDocument);
  }

  /**
   * Maps a journal line onto the preview body, or returns an already-flat
   * payload as-is.
   *
   * @param entry - Journal line (`gcs_path`) or preview payload
   * @returns Preview payload, or `null` when the path is blank
   */
  private static toPreview(
    entry: StoredDocumentEntry,
  ): CandidDocumentPayload | null {
    if (!isJournalDocument(entry)) {
      return entry.document_gcs_path ? entry : null;
    }
    if (!entry.gcs_path) {
      return null;
    }
    return {
      document_gcs_path: entry.gcs_path,
      display_name: entry.display_name || null,
    };
  }
}

/**
 * Stored column value: Candid dump (`gcs_path`) or already-flat preview body.
 */
type StoredDocumentEntry = CandidJournalDocumentEntry | CandidDocumentPayload;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  !!value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Narrows JSON to a stored document line (journal or already-flat preview).
 *
 * @param value - Unknown payload fragment
 * @returns Whether the value carries a GCS path under either field name
 */
const isStoredDocument = (value: unknown): value is StoredDocumentEntry => {
  const entry = asRecord(value);
  return !!entry && ("gcs_path" in entry || "document_gcs_path" in entry);
};

const isJournalDocument = (
  entry: StoredDocumentEntry,
): entry is CandidJournalDocumentEntry => "gcs_path" in entry;
